import React, { useEffect, useState } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";

import { useAuthMotion } from "./authMotion";
import styles from "./AuthLayout.module.css";

const KEYS = ["a", "b", "c", "d"];
const INTERVAL = 6500; // long enough to type, read, and pause before the next
const CHAR = 0.042; // seconds per character
const MAX_TYPE = 1.5; // cap, so a long Malay title doesn't crawl

const typeDuration = (text) => Math.min(text.length * CHAR, MAX_TYPE);

// The rotating brand copy: each slide types its title out, then the
// supporting line arrives underneath.
//
// Note this is a keyed element that animates IN rather than an
// AnimatePresence pair. mode="wait" holds the incoming slide until the
// outgoing one reports it has finished exiting, and nested inside the brand
// panel's variant tree that signal never arrived — the first slide stayed on
// screen while the state behind it kept advancing. Letting React swap the
// node has no such dependency.
//
// The viewport is min-height'd rather than auto: the slides wrap to different
// depths (more so in Malay, ~14% longer) and .brandInner is a space-between
// column, so without a floor the copyright below would jump on every change.
const BrandSlides = () => {
  const { t } = useTranslation(["auth"]);
  const v = useAuthMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [typing, setTyping] = useState(!v.reduced);

  const slide = KEYS[index];
  const title = t(`auth:brand.slides.${slide}.title`);
  const text = t(`auth:brand.slides.${slide}.text`);
  const typeFor = typeDuration(title);

  // Character count drives the visible slice. A motion value rather than
  // component state so the reveal runs off framer's frame loop instead of
  // re-rendering React on every character.
  const count = useMotionValue(0);
  const typed = useTransform(count, (n) => title.slice(0, Math.round(n)));

  useEffect(() => {
    if (v.reduced) {
      count.set(title.length);
      setTyping(false);
      return undefined;
    }
    setTyping(true);
    count.set(0);
    const controls = animate(count, title.length, {
      duration: typeFor,
      ease: "linear",
      onComplete: () => setTyping(false),
    });
    return () => controls.stop();
  }, [title, typeFor, v.reduced, count]);

  // Auto-advance is off under reduced motion — text that rewrites itself is
  // exactly the unrequested movement that setting asks us to stop. The dots
  // remain, so every slide is still reachable by hand.
  const auto = !v.reduced && !paused;

  useEffect(() => {
    if (!auto) return undefined;
    const id = setInterval(() => setIndex((n) => (n + 1) % KEYS.length), INTERVAL);
    return () => clearInterval(id);
  }, [auto]);

  return (
    <div
      className={styles.slides}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Indicators sit ABOVE the copy on purpose. .slideViewport carries a
          min-height floor so the blocks below it don't jump as slides of
          different depths rotate; with the dots underneath, that slack showed
          up as a gap between the description and the dots. Above the title,
          the slack falls at the end of the copy where it reads as ordinary
          spacing. */}
      <div className={styles.dots}>
        {KEYS.map((k, i) => (
          <button
            key={k}
            type="button"
            className={`${styles.dot} ${i === index ? styles.dotOn : ""}`}
            onClick={() => setIndex(i)}
            aria-label={String(i + 1)}
            aria-current={i === index ? "true" : undefined}
          />
        ))}
      </div>

      <div className={styles.slideViewport}>
        <div key={index}>
          <p className={styles.welcome}>
            {/* The animated half is hidden from assistive tech — a partial
                word is noise. The full title sits beside it, off-screen. */}
            <span aria-hidden="true">
              <motion.span>{typed}</motion.span>
              {typing && <span className={styles.caret} />}
            </span>
            <span className={styles.srOnly}>{title}</span>
          </p>

          {/* Waits for the title to finish typing, then follows it in. */}
          <motion.p
            className={styles.tagline}
            initial={v.reduced ? { opacity: 0 } : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: v.reduced ? 0.18 : 0.45,
              delay: v.reduced ? 0 : typeFor + 0.12,
              ease: "easeOut",
            }}
          >
            {text}
          </motion.p>
        </div>
      </div>

    </div>
  );
};

export default BrandSlides;
